import * as React from "react";
import { styled } from "@mui/material/styles";
import SearchForm from "../../../containers/SearchForm";
import {
  Form,
  FormSelectChoice,
  KeyValue,
  KeyValueObject,
  Query,
} from "../../../interfaces";
import {
  Typography,
  List,
  ListItem,
  Divider,
  Grid,
  Button,
} from "@mui/material";
import SelectFilterField from "../../SelectFilterField";
import { useEffect, useState } from "react";
import {
  branchOfficeLocationOptions,
  branchOfficeSelectBanksChoices,
  branchOfficeSelectBHSChoices,
  numericalRegEx,
  sodSearchFieldInputLookup,
} from "../../../refdata/constants";
import useMinLengthErrorValidation from "../../../hooks/useMinLengthErrorValidation";
import useAppStateToFormChanges from "../../../hooks/useAppStateToFormChanges";
import {
  reduceObj,
  getExpandState,
  yearChoice,
} from "../../../common/services/Utilities";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import clsx from "clsx";
import TextFilterField from "../../TextFilterField";
import BankBHCsToggle from "../../BankBHCsToggle";
import AccordionDropdown from "../../../containers/AccordionDropdown";
import BranchOfficeMarketSelections from "../../../containers/BranchOfficeMarketSelections";
import useLastYearFilter from "../../../hooks/useLastYearFilter";
import MultiDropdownInput from "../../../containers/MultiDropdownInput";

const PREFIX = "BranchOfficeSearchForm";

const classes = {
  addRemoveButtons: `${PREFIX}-addRemoveButtons`,
  addButton: `${PREFIX}-addButton`,
  buttonsGrid: `${PREFIX}-buttonsGrid`,
  title: `${PREFIX}-title`,
};

const Root = styled(List)(({ theme }) => ({
  [`& .${classes.addRemoveButtons}`]: {
    "&.MuiButton-root": {
      width: "100%",
      padding: 0,
      fontWeight: 400,
      margin: 0,
    },
  },

  [`& .${classes.addButton}`]: {
    "&.MuiButton-root": {
      "backgroundColor": "#ffffff",
      "&:hover, &.Mui-focusVisible": {
        color: "#ffffff",
        backgroundColor: theme.palette.primary.main,
      },
    },
  },

  [`& .${classes.buttonsGrid}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.title}`]: { paddingTop: 16, lineHeight: "1rem" },
}));

export interface BranchOfficeForm extends Form {
  cert: string;
  zipbr: string;
  state: string;
  stateNames: string[];
  name: string;
  city: string;
  locations: string;
  msa: string;
  bhcName: string;
  bhcRSSD: string;
  lastYear: string;
  marketSelection: KeyValue[];
  institutionType: string;
}

const divider = (
  <Divider
    style={{ margin: 0, backgroundColor: "#acc8df" }}
    variant="middle"
    component="li"
  />
);

export default function BranchOfficeSearchForm({
  appState,
  updateFilter,
  disableAdvancedSearch,
  isCountyView,
}: {
  appState: Query;
  updateFilter: (filterField: string, value: string | number) => void;
  disableAdvancedSearch?: Boolean;
  isCountyView?: Boolean;
}) {
  const { lastYear } = useLastYearFilter(appState.filters, updateFilter);
  const filters = {
    cert: appState.filters.cert,
    zipbr: appState.filters.zipbr,
    OTSDocket: appState.filters.OTSDocket,
    rssdid: appState.filters.rssdid,
    stalp: appState.filters.stalp,
    state: appState.filters.stname,
    stateNames: appState.filters.stateNames,
    msa: appState.filters.msa,
    bhcRSSD: appState.filters.bhcRSSD,
    lastYear: appState.filters.lastYear,
  };
  const search = {
    name: appState.search.name,
    bhcName: appState.search.bhcName,
    city: appState.search.city,
  };
  const initialFormState = {
    ...filters,
    ...search,
    locations: appState.ui.locations,
    institutionType: appState.ui.institutionType,
    marketSelection: appState.ui.marketSelection,
  };

  const [reset, setReset] = useState(false);

  useEffect(() => {
    if (reset) {
      updateFilter("stalpbr", "");
      updateFilter("cert", "");
    }
  }, [reset, updateFilter]);
  useEffect(() => {
    if (appState.filters.stalpbr === "") {
      setReset(false);
    }
  }, [appState]);
  const bankFormValues = ["cert", "name"];
  const bhcFormValues = ["bhcName", "bhcRSSD"];

  const [branchOfficeSearchForm, setBranchOfficeSearchForm] =
    useState<BranchOfficeForm>(initialFormState);
  const [marketErrors, setMarketErrors] = useState<boolean[]>([false]);
  const [msaValues, setMsaValues] = useState<FormSelectChoice[]>([]);
  React.useEffect(() => {
    if (branchOfficeSearchForm.stateNames?.length !== marketErrors?.length) {
      setMarketErrors(
        new Array(branchOfficeSearchForm.stateNames?.length).fill(false),
      );
    }
  }, [branchOfficeSearchForm.stateNames?.length, marketErrors?.length]);

  const branchOfficeReportingPeriods = yearChoice(1994, lastYear, "06/30/");

  const branchOfficeErrorMetaData = {
    name: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
    bhcName: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
      uniqueField: "NAMEHCR",
    },
    bhcRSSD: {
      minLength: 6,
      helperText: "Must be a 6 - 7 Digit Number",
    },
  };

  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [disabled, setDisabled] = useState<boolean>(false);

  const disableNameState = React.useMemo(() => {
    return {
      year: branchOfficeSearchForm.lastYear,
      name: branchOfficeSearchForm.name,
      bhcName: branchOfficeSearchForm.bhcName,
    };
  }, [
    branchOfficeSearchForm.bhcName,
    branchOfficeSearchForm.name,
    branchOfficeSearchForm.lastYear,
  ]);
  const [namePrevState, setNamePrevState] = React.useState(disableNameState);
  useEffect(() => {
    if (
      JSON.stringify(namePrevState) !== JSON.stringify(disableNameState) &&
      errors.size === 0
    ) {
      setDisabled(false);
      setNamePrevState(disableNameState);
    } else {
      setDisabled(
        errors.size !== 0 ||
          marketErrors.includes(true) ||
          (branchOfficeSearchForm.locations === "marketSelect" &&
            !branchOfficeSearchForm.stateNames[0]),
      );
    }
  }, [
    marketErrors,
    errors,
    branchOfficeSearchForm.locations,
    branchOfficeSearchForm.stateNames,
    disableNameState,
    namePrevState,
  ]);

  useMinLengthErrorValidation(
    branchOfficeSearchForm,
    branchOfficeErrorMetaData,
    setErrors,
  );

  const { prevForm, setPrevForm } = useAppStateToFormChanges<BranchOfficeForm>(
    appState.filters,
    appState.search,
    setBranchOfficeSearchForm,
  );

  React.useEffect(() => {
    if (
      branchOfficeSearchForm.bhcName &&
      (branchOfficeSearchForm.OTSDocket || branchOfficeSearchForm.rssdid)
    ) {
      setBranchOfficeSearchForm({
        ...branchOfficeSearchForm,
        OTSDocket: "",
        rssdid: "",
      });
    }
    if (
      branchOfficeSearchForm.locations === "msa" &&
      (branchOfficeSearchForm.zipbr ||
        Object.keys(branchOfficeSearchForm.marketSelection[0]).length)
    ) {
      setBranchOfficeSearchForm({
        ...branchOfficeSearchForm,
        zipbr: "",
        marketSelection: [{}],
      });
    }
    if (
      branchOfficeSearchForm.locations === "zip" &&
      (branchOfficeSearchForm.msa ||
        Object.keys(branchOfficeSearchForm.marketSelection[0]).length)
    ) {
      setBranchOfficeSearchForm({
        ...branchOfficeSearchForm,
        msa: "",
        marketSelection: [{}],
      });
    }
    if (
      branchOfficeSearchForm.locations === "marketSelect" &&
      (branchOfficeSearchForm.msa || branchOfficeSearchForm.zipbr)
    ) {
      setBranchOfficeSearchForm({
        ...branchOfficeSearchForm,
        msa: "",
        zipbr: "",
      });
    }
  }, [branchOfficeSearchForm]);
  React.useEffect(() => {
    if (
      branchOfficeSearchForm.locations === "msa" &&
      JSON.stringify(branchOfficeSearchForm) !== JSON.stringify(prevForm)
    ) {
      let isMounted = true;
      const cert = branchOfficeSearchForm.cert
        ? `CERT:${branchOfficeSearchForm.cert} AND `
        : "";
      const docket = branchOfficeSearchForm.OTSDocket
        ? `DOCKET:${branchOfficeSearchForm.OTSDocket} AND `
        : "";
      const rssdid = branchOfficeSearchForm.rssdid
        ? `RSSDID:${branchOfficeSearchForm.rssdid} AND `
        : "";
      const bhcRSSD = branchOfficeSearchForm.bhcRSSD
        ? `RSSDHCR:${branchOfficeSearchForm.bhcRSSD} AND `
        : "";
      const bhcName = branchOfficeSearchForm.bhcName
        ? `NAMEHCR:${branchOfficeSearchForm.bhcName}`
        : "";
      const bankName = branchOfficeSearchForm.name
        ? `NAME:${branchOfficeSearchForm.name}`
        : "";
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=${cert}${bhcRSSD}${docket}${rssdid}YEAR:${
            branchOfficeSearchForm.lastYear
          }&search=${bhcName}${bankName}&agg_by=MSANAMB&agg_limit=10000`,
        )
          .then((response) => {
            if (isMounted) {
              setMsaValues([
                { label: "Select MSA", value: "" },
                ...response.data
                  .map((e: KeyValue) => {
                    return {
                      label: e.MSANAMB,
                      value: e.MSANAMB,
                    };
                  })
                  .filter((value: KeyValue) => value.value),
              ]);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [branchOfficeSearchForm, prevForm]);

  const getInitialSearchField = () => {
    return appState.search.name
      ? "name"
      : appState.filters.cert
        ? "cert"
        : appState.filters.OTSDocket
          ? "OTSDocket"
          : appState.filters.rssdid
            ? "rssdid"
            : "";
  };
  const getInitialBHCsSearchField = () => {
    return appState.search.bhcname
      ? "bhcName"
      : appState.filters.bhcRSSD
        ? "bhcRSSD"
        : "";
  };
  const institutionTypeSelection = (
    <AccordionDropdown
      id="institutionType"
      title="Institution Type"
      defaultExpand={getExpandState(appState.ui.institutionTypeExpand, true)}
      body={
        <BankBHCsToggle
          form={branchOfficeSearchForm}
          setForm={setBranchOfficeSearchForm}
          bankValues={bankFormValues}
          bhcValues={bhcFormValues}
        />
      }
    />
  );

  const currentFormFilters = Object.keys(branchOfficeSearchForm)
    .map((key) => {
      return Object.keys(filters).includes(key)
        ? ({ key, value: branchOfficeSearchForm[key] } as KeyValueObject)
        : { key: "", value: "" };
    })
    .filter((value) => value.key !== "");
  const currentFormSearches = Object.keys(branchOfficeSearchForm)
    .map((key) => {
      return Object.keys(search).includes(key)
        ? ({ key, value: branchOfficeSearchForm[key] } as KeyValueObject)
        : { key: "", value: "" };
    })
    .filter((value) => value.key !== "");
  const BranchOfficeFromList = (
    <Root>
      <ListItem>{institutionTypeSelection}</ListItem>
      {!disableAdvancedSearch && (
        <>
          <ListItem>
            <Typography className={classes.title} variant="h4">
              {branchOfficeSearchForm.institutionType === "banks"
                ? "Name / Cert / ID"
                : "Bank Holding Co. Name/ID"}
            </Typography>
          </ListItem>
          <ListItem style={{ display: "block" }}>
            {branchOfficeSearchForm.institutionType === "banks" && (
              <MultiDropdownInput
                selectId="name-cert-id"
                form={branchOfficeSearchForm}
                setForm={setBranchOfficeSearchForm}
                initialSearchFieldName={getInitialSearchField()}
                formSelectChoices={branchOfficeSelectBanksChoices}
                searchFieldInputLookup={sodSearchFieldInputLookup}
                errors={errors}
                formMetaData={branchOfficeErrorMetaData}
                autocompleteFilters={`YEAR:${branchOfficeSearchForm.lastYear}`}
              />
            )}
            {branchOfficeSearchForm.institutionType === "bhcs" && (
              <MultiDropdownInput
                selectId="bhcName-rssdid"
                form={branchOfficeSearchForm}
                setForm={setBranchOfficeSearchForm}
                initialSearchFieldName={getInitialBHCsSearchField()}
                formSelectChoices={branchOfficeSelectBHSChoices}
                searchFieldInputLookup={sodSearchFieldInputLookup}
                errors={errors}
                formMetaData={branchOfficeErrorMetaData}
                autocompleteFilters={`YEAR:${branchOfficeSearchForm.lastYear}`}
              />
            )}
          </ListItem>
          {divider}
        </>
      )}

      <ListItem>
        <Typography className={classes.title} variant="h4">
          Reporting Period Ending
        </Typography>
      </ListItem>
      <ListItem>
        <SelectFilterField
          currentValue={branchOfficeSearchForm.lastYear}
          fieldName="lastYear"
          options={branchOfficeReportingPeriods}
          form={branchOfficeSearchForm}
          setForm={setBranchOfficeSearchForm}
        />
      </ListItem>
      {divider}
      {!disableAdvancedSearch && (
        <>
          {" "}
          <ListItem>
            <List>
              <ListItem>
                <Typography className={classes.title} variant="h4">
                  Locations
                </Typography>
                <SelectFilterField
                  currentValue={branchOfficeSearchForm.locations}
                  fieldName="locations"
                  options={branchOfficeLocationOptions}
                  form={branchOfficeSearchForm}
                  setForm={setBranchOfficeSearchForm}
                />
              </ListItem>
              {branchOfficeSearchForm.locations === "marketSelect" &&
                branchOfficeSearchForm.stateNames.map(
                  (state: string, int: number) => {
                    return (
                      <React.Fragment key={int}>
                        <BranchOfficeMarketSelections
                          filtersAndSearch={{
                            filters: reduceObj(currentFormFilters),
                            search: reduceObj(currentFormSearches),
                          }}
                          currentIndex={int}
                          searchForm={branchOfficeSearchForm}
                          setSearchForm={setBranchOfficeSearchForm}
                          errors={marketErrors}
                          setErrors={setMarketErrors}
                          institutionType={
                            branchOfficeSearchForm.institutionType
                          }
                        />
                        <Grid
                          container
                          spacing={2}
                          className={classes.buttonsGrid}
                        >
                          <Grid
                            size={{
                              sm: 6,
                            }}
                          >
                            {branchOfficeSearchForm.stateNames.length > 1 && (
                              <Button
                                color="secondary"
                                variant="contained"
                                className={classes.addRemoveButtons}
                                onClick={() =>
                                  setBranchOfficeSearchForm({
                                    ...branchOfficeSearchForm,
                                    stateNames:
                                      branchOfficeSearchForm.stateNames.filter(
                                        (state: string, index: number) =>
                                          index !== int,
                                      ),
                                    marketSelection:
                                      branchOfficeSearchForm.marketSelection.filter(
                                        (selection: KeyValue, index: number) =>
                                          index !== int,
                                      ),
                                  })
                                }
                              >
                                Remove
                              </Button>
                            )}
                          </Grid>
                          {(appState.filters.cert !== "" ||
                            appState.filters.bhcRSSD !== "" ||
                            Object.keys(
                              branchOfficeSearchForm.marketSelection[0],
                            ).length > 0) &&
                            branchOfficeSearchForm.stateNames[int] !== "" &&
                            branchOfficeSearchForm.stateNames[0] !== "" &&
                            branchOfficeSearchForm.stateNames[0] !== "ALL" && (
                              <Grid
                                size={{
                                  sm: 6,
                                }}
                              >
                                <Button
                                  color="secondary"
                                  variant="contained"
                                  className={clsx(
                                    classes.addRemoveButtons,
                                    classes.addButton,
                                  )}
                                  onClick={() =>
                                    setBranchOfficeSearchForm({
                                      ...branchOfficeSearchForm,
                                      stateNames: [
                                        ...branchOfficeSearchForm.stateNames,
                                        "",
                                      ],
                                    })
                                  }
                                >
                                  Add Another
                                </Button>
                              </Grid>
                            )}
                        </Grid>
                      </React.Fragment>
                    );
                  },
                )}
              {branchOfficeSearchForm.locations === "msa" && (
                <ListItem>
                  <SelectFilterField
                    currentValue={branchOfficeSearchForm.msa}
                    fieldName="msa"
                    options={msaValues}
                    form={branchOfficeSearchForm}
                    setForm={setBranchOfficeSearchForm}
                  />
                </ListItem>
              )}
              {branchOfficeSearchForm.locations === "zip" && (
                <ListItem>
                  <TextFilterField
                    currentValue={branchOfficeSearchForm.zipbr}
                    apiPath="institutions"
                    fieldName="zipbr"
                    placeholder="Enter ZIP Code"
                    regEx={numericalRegEx}
                    form={branchOfficeSearchForm}
                    setForm={setBranchOfficeSearchForm}
                  />
                </ListItem>
              )}
            </List>
          </ListItem>
          {divider}
        </>
      )}
    </Root>
  );
  return (
    <>
      <SearchForm
        scroll
        prevForm={prevForm}
        setPrevForm={setPrevForm}
        canHideSearchPanel={false}
        formList={BranchOfficeFromList}
        form={branchOfficeSearchForm}
        disabled={disabled}
        searchButtonText={"Generate Report"}
        displayReset={!!isCountyView}
        onReset={() => setReset(true)}
      />
    </>
  );
}
